import { Form, Input, Select } from "antd";
import { AppButton } from "components/button/AppButton";
import avatar from "assets/images/default.webp";
import {
  generalValidationRules,
  textInputValidationRules,
  aboutCompanyValidationRule,
  companySizeValidationRule,
  validateURL,
} from "utils/formHelpers/validations";
import {
  QUERY_KEY_FOR_RECRUITER_PROFILE,
  useGetRecruiterProfile,
} from "../hooks/useGetRecruiterProfile";
import { useEffect, useState } from "react";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useUpdateRecruiterProfile } from "../hooks/useUpdateRecruiterProfile";
import { openNotification } from "utils/notification";
import { useQueryClient } from "react-query";
import { IRecruiterProfile } from "../types";
import { FileWithPreview } from "features/settings/types";
import { COUNTRIES, LANGUAGES } from "constants/general";
import { useGetSectors } from "features/jobs/hooks/useGetSectors";
import { resizeImageToSquare, resizeCoverPhoto } from "utils/resizephoto";
import { ChangeEvent } from "react";

const CITIES_API_URL = "https://countriesnow.space/api/v0.1/countries/cities";
const DEFAULT_COVER = "/assets/cover_image.png";

export const ProfileCompanySettings = () => {
  const { data: profileData, isLoading } = useGetRecruiterProfile();
  const { userInfo, token } = useGetUserInfo();
  const [form] = Form.useForm();
  const { mutate, isLoading: loadUpdate } = useUpdateRecruiterProfile();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [file, setFile] = useState<FileWithPreview | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [resizedBlob, setResizedBlob] = useState<Blob | null>(null);
  const [coverPhotoBlob, setCoverPhotoBlob] = useState<Blob | null>(null);
  const { data: sectorData, isLoading: loadingSector } = useGetSectors();
  const [filteredCities, setFilteredCities] = useState<any[]>([]);
  const [bio, setBio] = useState("");

  useEffect(() => {
    if (profileData?.data) {
      const shortBio = profileData.data.short_bio || "";
      setBio(shortBio);
      form.setFieldsValue({ short_bio: shortBio });
    }
  }, [profileData, form]);

  useEffect(() => {
    const userCountry = profileData?.data.country;

    const languageArray =
      profileData?.data?.language && Array.isArray(profileData.data.language)
        ? profileData.data.language
        : [];

    form.setFieldsValue({
      company_name: profileData?.data.company_name,
      rc_number: profileData?.data.rc_number,
      phone: profileData?.data.phone,
      country: profileData?.data.country,
      city: profileData?.data.city,
      language: languageArray,
      sector_id: profileData?.data.sector_id
        ? String(profileData?.data.sector_id)
        : undefined,
      linkedin: profileData?.data.linkedin,
      company_website: profileData?.data.company_website,
      short_bio: profileData?.data.short_bio,
      company_size: profileData?.data.company_size,
    });

    if (userCountry) {
      setSelectedCountry(userCountry);
      fetchCities(userCountry);
    }
  }, [form, profileData]);

  const handleSubmit = (data: any) => {
    const props: IRecruiterProfile = {
      first_name: profileData?.data.first_name || "",
      middle_name: profileData?.data.middle_name || "",
      last_name: profileData?.data.last_name || "",
      company_name: data.company_name,
      rc_number: data.rc_number || "",
      photo: resizedBlob ? resizedBlob : null,
      cover_photo: coverPhotoBlob ? coverPhotoBlob : null,
      phone: data.phone || "",
      country: data.country || "",
      city: data.city || "",
      sector_id: data.sector_id || "",
      language: data.language || [],
      prefered_industries: profileData?.data.prefered_industries,
      prefered_locations: profileData?.data.prefered_locations,
      company_website: data.company_website || "",
      linkedin: data.linkedin || "",
      github: profileData?.data.github || "",
      behance: profileData?.data.behance || "",
      facebook: profileData?.data.facebook || "",
      x: profileData?.data.x || "",
      instagram: profileData?.data.instagram || "",
      short_bio: data.short_bio || "",
      company_size: data.company_size || "",
      token,
    };

    mutate(props, {
      onError: (err: any) => {
        openNotification({
          title: "Error",
          state: "error",
          description: "Error occurred try again",
          duration: 4.5,
        });
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Profile Updated Successfully",
          duration: 4.5,
        });
        setIsEditing(false);
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_RECRUITER_PROFILE],
        });
      },
    });
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile({
        ...selectedFile,
        preview: URL.createObjectURL(selectedFile),
      } as FileWithPreview);
      try {
        const resizedImageBlob = await resizeImageToSquare(selectedFile, 200);
        setResizedBlob(resizedImageBlob);
      } catch (error) {
        console.error("Error resizing image:", error);
      }
    }
  };

  const coverPhotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile({
        ...selectedFile,
        preview: URL.createObjectURL(selectedFile),
      } as FileWithPreview);
      try {
        const resizedImageBlob = await resizeCoverPhoto(selectedFile);
        setCoverPhotoBlob(resizedImageBlob);
      } catch (error) {
        console.error("Error resizing image:", error);
      }
    }
  };

  const isCorporate =
    userInfo?.recruiter?.is_corporate === 1 ||
    userInfo?.recruiter?.is_corporate === "1";
  const corporate_id = isCorporate
    ? userInfo?.current_team?.corporate_id || userInfo?.recruiter?.id || null
    : null;

  const maxChars = 1000;
  const isOverLimit = bio.length > maxChars;

  const fetchCities = async (countryCode: string) => {
    try {
      const response = await fetch(CITIES_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          country: countryCode ? countryCode : "NG",
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setFilteredCities(data.data);
      } else {
        console.error("Failed to fetch cities:", data);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    const country = COUNTRIES.find((country) => country.name === value);
    if (country) {
      fetchCities(country.name);
    }
  };

  if (!isEditing) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="font-semibold text-lg text-accent">
              Company Details
            </h1>
            <h3 className="text-sm text-gray-500">
              View your company information
            </h3>
          </div>
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <i className="ri-edit-line"></i>
            <span>Edit</span>
          </button>
        </div>
        <hr className="mb-6" />

        {/* Cover & Profile Photos */}
        <div className="relative mb-12 rounded-lg overflow-hidden bg-gray-100">
          <img
            src={profileData?.data.user?.cover_photo || DEFAULT_COVER}
            alt="cover"
            className="w-full h-48 object-cover"
          />
          <div className="absolute -bottom-6 left-4">
            <img
              src={profileData?.data.dp || avatar}
              alt="avatar"
              className="h-20 w-20 rounded-full border-2 border-white shadow-lg object-cover"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <h2 className="font-semibold text-gray-900">Company Information</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company Name
                </label>
                <p className="mt-1 text-gray-900">
                  {profileData?.data.company_name || "—"}
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  RC Number
                </label>
                <p className="mt-1 text-gray-900">
                  {profileData?.data.rc_number || "—"}
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone Number
                </label>
                <p className="mt-1 text-gray-900">
                  {profileData?.data.phone || "—"}
                </p>
              </div>

              {corporate_id != null && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company Size
                  </label>
                  <p className="mt-1 text-gray-900">
                    {profileData?.data.company_size || "—"}
                  </p>
                </div>
              )}

              <div className="md:col-span-2">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {corporate_id != null ? "About Company" : "Short Bio"}
                </label>
                <p className="mt-1 text-gray-900 whitespace-pre-wrap">
                  {profileData?.data.short_bio || "—"}
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Country
                </label>
                <p className="mt-1 text-gray-900">
                  {profileData?.data.country || "—"}
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  City
                </label>
                <p className="mt-1 text-gray-900">
                  {profileData?.data.city || "—"}
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sector
                </label>
                <p className="mt-1 text-gray-900">
                  {sectorData?.find(
                    (s) => Number(s.id) === Number(profileData?.data.sector_id),
                  )?.name || "—"}
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company Website
                </label>
                {profileData?.data.company_website ? (
                  <a
                    href={profileData.data.company_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-blue-600 hover:underline block"
                  >
                    {profileData.data.company_website}
                  </a>
                ) : (
                  <p className="mt-1 text-gray-900">—</p>
                )}
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  LinkedIn Profile
                </label>
                {profileData?.data.linkedin ? (
                  <a
                    href={profileData.data.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-blue-600 hover:underline block"
                  >
                    {profileData.data.linkedin}
                  </a>
                ) : (
                  <p className="mt-1 text-gray-900">—</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // EDIT MODE
  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-semibold text-lg text-accent">
            Edit Company Details
          </h1>
          <h3 className="text-sm text-gray-500">
            Update your company information
          </h3>
        </div>
        <button
          onClick={() => setIsEditing(false)}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
      <hr className="mb-6" />

      <Form form={form} onFinish={handleSubmit}>
        {/* Cover & Profile Photos */}
        <div className="grid grid-cols-1 md:grid-cols-5 my-5 gap-3">
          <div className="relative col-span-5">
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              id="coverFileInput"
              onChange={coverPhotoChange}
            />
            <label htmlFor="coverFileInput" className="cursor-pointer block">
              <img
                src={
                  coverPhotoBlob
                    ? URL.createObjectURL(coverPhotoBlob)
                    : profileData?.data.user?.cover_photo || DEFAULT_COVER
                }
                alt="cover"
                className="w-full h-48 object-cover rounded-lg"
              />
            </label>
            <div className="absolute -bottom-6 left-4">
              <label htmlFor="profileFileInput" className="cursor-pointer">
                <img
                  src={
                    resizedBlob
                      ? URL.createObjectURL(resizedBlob)
                      : profileData?.data.dp || avatar
                  }
                  alt="avatar"
                  className="h-20 w-20 rounded-full border-2 border-white shadow-lg"
                />
              </label>
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                id="profileFileInput"
                onChange={handleFileChange}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <h2 className="font-semibold text-gray-900">Company Information</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Form.Item
                name="company_name"
                label="Company Name"
                rules={textInputValidationRules}
              >
                <Input placeholder="Company name" size="large" />
              </Form.Item>

              <Form.Item name="rc_number" label="RC Number">
                <Input placeholder="RC Number" size="large" />
              </Form.Item>

              <Form.Item name="phone" label="Phone Number">
                <Input placeholder="Phone number" size="large" />
              </Form.Item>

              {corporate_id != null && (
                <Form.Item
                  name="company_size"
                  label="Company Size"
                  rules={[...generalValidationRules, companySizeValidationRule]}
                >
                  <Input placeholder="Company Size" size="large" />
                </Form.Item>
              )}

              <div className="md:col-span-2">
                <Form.Item
                  name="short_bio"
                  label={corporate_id != null ? "About Company" : "Short Bio"}
                  rules={
                    corporate_id != null ? [aboutCompanyValidationRule] : []
                  }
                >
                  <Input.TextArea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder={
                      corporate_id != null
                        ? "About Company"
                        : "Write a short bio about yourself"
                    }
                    rows={4}
                  />
                </Form.Item>
                <p
                  className={`text-xs mt-1 ${isOverLimit ? "text-red-500" : "text-gray-500"}`}
                >
                  {isOverLimit
                    ? `⚠️ You have exceeded the ${maxChars}-character limit.`
                    : `${maxChars - bio.length} characters remaining`}
                </p>
              </div>

              <Form.Item
                name="country"
                label="Country"
                rules={generalValidationRules}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select"
                  size="large"
                  options={COUNTRIES?.map((country) => ({
                    value: country.name,
                    label: country.name,
                  }))}
                  onChange={handleCountryChange}
                />
              </Form.Item>

              <Form.Item
                name="city"
                label="City"
                rules={generalValidationRules}
              >
                <Select
                  options={filteredCities.map((city) => ({
                    value: city,
                    label: city,
                  }))}
                  allowClear
                  showSearch
                  placeholder="Select"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="sector_id"
                label="Sector"
                rules={generalValidationRules}
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="Select"
                  size="large"
                  loading={loadingSector}
                  options={sectorData?.map((sector) => ({
                    value: String(sector.id),
                    label: sector.name,
                  }))}
                />
              </Form.Item>

              <Form.Item
                name="company_website"
                label="Company Website (Optional)"
                rules={[{ validator: validateURL }]}
              >
                <Input placeholder="https://google.com" size="large" />
              </Form.Item>

              <Form.Item
                name="linkedin"
                label="LinkedIn profile (Optional)"
                rules={[{ validator: validateURL }]}
              >
                <Input placeholder="https://linkedin.com" size="large" />
              </Form.Item>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <AppButton
                type="submit"
                label="Save Changes"
                isLoading={loadUpdate}
                additionalClassNames={[
                  "px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700",
                ]}
              />
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};
